import { apiClient } from './client';
import { AiDisabledError } from './documents.api';
import { eosErrorCode, eosErrorMessage } from '@/lib/eos/errors';
import type { TipTapDoc } from '@/components/compliance/SopRenderer';

// Re-export the shared AI-disabled sentinel (thrown on 503 `ai_disabled`) so
// callers can `import { AiDisabledError } from '@/api/sops.api'` alongside the
// SOP applicability helpers.
export { AiDisabledError };

// ── Response shapes — kept in lockstep with server/src/routes/compliance/sops.routes.ts ──

export type SopFamily = 'ADM' | 'ISO' | 'PAC' | 'QUA' | 'REC' | 'SSOP' | 'KQM';
export type SopVersionState = 'draft' | 'pending_approval' | 'approved' | 'superseded';
export type ServiceLineTag = 'kwf' | 'kwpl';

export interface SopListItem {
  id: string;
  family: SopFamily;
  number: number;
  slug: string;
  title: string;
  serviceLineTags: ServiceLineTag[];
  importedFromFilename: string | null;
  latestVersion: {
    id: string;
    revision: number;
    state: SopVersionState;
    publishedAt: string | null;
    createdAt: string;
  } | null;
  createdAt: string;
}

export type SopDetail = Omit<SopListItem, 'latestVersion'> & {
  currentVersion: {
    id: string;
    revision: number;
    state: SopVersionState;
    contentJson: TipTapDoc;
    changeSummary: string | null;
    reasonForChange: string | null;
    publishedAt: string | null;
    createdAt: string;
  };
  history: {
    id: string;
    revision: number;
    state: SopVersionState;
    publishedAt: string | null;
    createdAt: string;
  }[];
  /**
   * Role / seat / department applicability (ADR-037). Only populated for
   * compliance authors; the employee (`employeeView`) shape omits it, so treat
   * it as optional and guard with `sop.applicability ?? []`.
   */
  applicability?: ApplicabilityView[];
};

// ── Applicability + mass upload (ADR-037) ──────────────────────────────────
//
// A SOP's *applicability* answers "who does this procedure apply to?" —
// resolved against roles / seats / departments. Slice 2 adds a mass-upload
// path (parse many `.docx` at once) and an AI mapper that suggests the
// applicability for each. These shapes stay in lockstep with the server
// contract in `server/src/modules/sops/*` + `sops.routes.ts`.

export type SopTargetType = 'all' | 'role' | 'seat' | 'department';

/** A resolved applicability row as returned by the server (read shape). */
export interface ApplicabilityView {
  id: string;
  targetType: SopTargetType;
  targetRole: string | null;
  targetSeatId: string | null;
  targetDepartmentId: string | null;
  /** Human-readable label, e.g. `"Warehouse Lead (seat)"`. */
  targetLabel: string;
  aiSuggested: boolean;
}

/** The write shape for a single applicability target. */
export interface SopApplicabilityInput {
  targetType: SopTargetType;
  targetRole?: string | null;
  targetSeatId?: string | null;
  targetDepartmentId?: string | null;
  /** Marks a target that originated from an accepted AI suggestion. */
  aiSuggested?: boolean;
}

/** An AI-suggested applicability target (never `all`). */
export interface ApplicabilitySuggestion {
  targetType: 'role' | 'seat' | 'department';
  targetRole: string | null;
  targetSeatId: string | null;
  targetDepartmentId: string | null;
  targetLabel: string;
  reason: string;
}

/** One file's parse result from `POST /bulk/parse`. */
export interface BulkParseFileResult {
  fileObjectId: string;
  filename: string;
  ok: boolean;
  error?: string;
  parsed?: {
    family: SopFamily | null;
    number: number | null;
    revision: number | null;
    title: string;
  };
  extractionWarnings?: string[];
  suggestions: ApplicabilitySuggestion[];
  aiStatus: 'ok' | 'disabled' | 'error';
}

/** One item to create in `POST /bulk`. */
export interface BulkCreateItem {
  fileObjectId: string;
  family: SopFamily;
  /** Omit to auto-assign the next number in the family. */
  number?: number;
  title: string;
  serviceLineTags?: ServiceLineTag[];
  changeSummary?: string | null;
  applicability: SopApplicabilityInput[];
}

export interface BulkCreatedSop {
  fileObjectId: string;
  slug: string;
  family: SopFamily;
  number: number;
  title: string;
}

export interface BulkFailedSop {
  fileObjectId: string;
  error: string;
}

export interface BulkCreateResult {
  created: BulkCreatedSop[];
  failed: BulkFailedSop[];
}

export async function listSops(): Promise<SopListItem[]> {
  const res = await apiClient.get<{ data: SopListItem[] }>('/api/compliance/sops');
  return res.data.data;
}

export async function getSop(slug: string, opts?: { revision?: number }): Promise<SopDetail> {
  const params = opts?.revision ? `?revision=${String(opts.revision)}` : '';
  const res = await apiClient.get<{ data: SopDetail }>(
    `/api/compliance/sops/${encodeURIComponent(slug)}${params}`,
  );
  return res.data.data;
}

/** Format a SopListItem's identifier for display: "QUA-0001" or "QUA-0001.002". */
export function formatSopIdentifier(item: { family: SopFamily; number: number; revision?: number }): string {
  const base = `${item.family}-${String(item.number).padStart(4, '0')}`;
  return item.revision ? `${base}.${String(item.revision).padStart(3, '0')}` : base;
}

// ── Service-line tags ────────────────────────────────────────────────────

export interface UpdateTagsRequest {
  serviceLineTags: ServiceLineTag[];
  reasonForChange: string;
}

export interface UpdateTagsResponse {
  id: string;
  slug: string;
  serviceLineTags: ServiceLineTag[];
}

export async function updateSopTags(slug: string, body: UpdateTagsRequest): Promise<UpdateTagsResponse> {
  const res = await apiClient.put<{ data: UpdateTagsResponse }>(
    `/api/compliance/sops/${encodeURIComponent(slug)}/tags`,
    body,
  );
  return res.data.data;
}

/** Human-readable label for a service-line tag. */
export function serviceLineLabel(tag: ServiceLineTag): string {
  switch (tag) {
    case 'kwf':
      return 'KWF — Fulfillment';
    case 'kwpl':
      return 'KWPL — Private Label';
  }
}

// ── Authoring + approval workflow (ADR-036) ────────────────────────────────
//
// Every mutation carries a `reasonForChange` (cGMP §111.180(c) / Part 11
// §11.10(e)) and returns the FULL updated `SopDetail` so the caller can
// refresh the page in a single round-trip. Kept in lockstep with the server
// contract in `server/src/routes/compliance/sops.routes.ts`.

export interface CreateSopRequest {
  family: SopFamily;
  /** Omit to auto-assign the next number in the family. */
  number?: number;
  title: string;
  contentJson: TipTapDoc;
  serviceLineTags?: ServiceLineTag[];
  changeSummary?: string;
  reasonForChange: string;
}

export interface OpenSopRevisionRequest {
  /** Defaults server-side to a clone of the approved revision's content. */
  contentJson?: TipTapDoc;
  changeSummary?: string;
  reasonForChange: string;
}

export interface EditSopDraftRequest {
  contentJson?: TipTapDoc;
  changeSummary?: string;
  reasonForChange: string;
}

export interface SubmitSopVersionRequest {
  reasonForChange: string;
}

export interface ApproveSopVersionRequest {
  /** Must match the approver's full name on file (Part 11 signature). */
  signatureName: string;
  reasonForChange: string;
}

export interface RejectSopVersionRequest {
  reasonForChange: string;
}

/** Create a SOP together with its first (draft) revision. */
export async function createSop(body: CreateSopRequest): Promise<SopDetail> {
  const res = await apiClient.post<{ data: SopDetail }>('/api/compliance/sops', body);
  return res.data.data;
}

/** Open a new draft revision on an existing SOP. */
export async function openSopRevision(slug: string, body: OpenSopRevisionRequest): Promise<SopDetail> {
  const res = await apiClient.post<{ data: SopDetail }>(
    `/api/compliance/sops/${encodeURIComponent(slug)}/versions`,
    body,
  );
  return res.data.data;
}

/** Edit an in-progress draft revision's content / change summary. */
export async function editSopDraft(
  slug: string,
  revision: number,
  body: EditSopDraftRequest,
): Promise<SopDetail> {
  const res = await apiClient.put<{ data: SopDetail }>(
    `/api/compliance/sops/${encodeURIComponent(slug)}/versions/${String(revision)}`,
    body,
  );
  return res.data.data;
}

/** Move a draft revision into `pending_approval`. */
export async function submitSopVersion(
  slug: string,
  revision: number,
  body: SubmitSopVersionRequest,
): Promise<SopDetail> {
  const res = await apiClient.post<{ data: SopDetail }>(
    `/api/compliance/sops/${encodeURIComponent(slug)}/versions/${String(revision)}/submit`,
    body,
  );
  return res.data.data;
}

/** Approve a pending revision (Part 11 signature); supersedes the prior one. */
export async function approveSopVersion(
  slug: string,
  revision: number,
  body: ApproveSopVersionRequest,
): Promise<SopDetail> {
  const res = await apiClient.post<{ data: SopDetail }>(
    `/api/compliance/sops/${encodeURIComponent(slug)}/versions/${String(revision)}/approve`,
    body,
  );
  return res.data.data;
}

/** Send a pending revision back to `draft`. */
export async function rejectSopVersion(
  slug: string,
  revision: number,
  body: RejectSopVersionRequest,
): Promise<SopDetail> {
  const res = await apiClient.post<{ data: SopDetail }>(
    `/api/compliance/sops/${encodeURIComponent(slug)}/versions/${String(revision)}/reject`,
    body,
  );
  return res.data.data;
}

/** True when an error is the 503 `ai_disabled` envelope. */
function isAiDisabledError(err: unknown): boolean {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { status?: number; data?: { error?: unknown } } }).response;
    return response?.status === 503 && response.data?.error === 'ai_disabled';
  }
  return false;
}

/**
 * Replace a SOP's applicability set (role / seat / department targets).
 * Every mutation carries a `reasonForChange` (cGMP §111.180(c)) and returns
 * the full updated `SopDetail`.
 */
export async function setSopApplicability(
  slug: string,
  body: { applicability: SopApplicabilityInput[]; reasonForChange: string },
): Promise<SopDetail> {
  const res = await apiClient.put<{ data: SopDetail }>(
    `/api/compliance/sops/${encodeURIComponent(slug)}/applicability`,
    body,
  );
  return res.data.data;
}

/**
 * Ask the assistant which roles / seats / departments this SOP applies to.
 * Throws {@link AiDisabledError} when AI is switched off (503), and otherwise
 * re-throws the axios error (carrying `ai_could_not_suggest` / `ai_error`) for
 * the caller to map.
 */
export async function suggestSopApplicability(slug: string): Promise<ApplicabilitySuggestion[]> {
  try {
    const res = await apiClient.post<{ data: { suggestions: ApplicabilitySuggestion[] } }>(
      `/api/compliance/sops/${encodeURIComponent(slug)}/applicability/suggest`,
    );
    return res.data.data.suggestions;
  } catch (err) {
    if (isAiDisabledError(err)) throw new AiDisabledError();
    throw err;
  }
}

/**
 * Parse a batch of uploaded `.docx` files: extract metadata (family / number /
 * title) and best-effort AI applicability suggestions per file. Never rejects
 * for a single bad file — each result carries its own `ok` / `error`.
 */
export async function parseBulkSops(body: {
  files: { fileObjectId: string; filename: string }[];
}): Promise<BulkParseFileResult[]> {
  const res = await apiClient.post<{ data: { results: BulkParseFileResult[] } }>(
    '/api/compliance/sops/bulk/parse',
    body,
  );
  return res.data.data.results;
}

/**
 * Create many SOPs (each as a first draft revision) in one batch. Returns the
 * per-item outcome; one item failing (e.g. a number collision → `sop_exists`)
 * never aborts the rest.
 */
export async function bulkCreateSops(body: {
  items: BulkCreateItem[];
  reasonForChange: string;
}): Promise<BulkCreateResult> {
  const res = await apiClient.post<{ data: BulkCreateResult }>('/api/compliance/sops/bulk', body);
  return res.data.data;
}

/**
 * Map the SOP workflow routes' `{ error: code }` envelopes to copy the UI
 * can render inline or toast. Reuses the generic envelope readers.
 */
export function describeSopError(err: unknown, fallback: string): string {
  switch (eosErrorCode(err)) {
    case 'sop_exists':
      return 'A SOP with that family and number already exists — pick another number or leave it blank to auto-assign.';
    case 'open_revision_exists':
      return 'A draft or pending revision is already open — finish or reject it before starting a new one.';
    case 'not_editable':
      return 'Only a draft revision can be edited. Open a new revision to make further changes.';
    case 'invalid_state':
      return (
        eosErrorMessage(err) ??
        'That action is not available in the revision’s current state — refresh the page.'
      );
    case 'cannot_approve_own_work':
      return 'You drafted this revision, so you cannot approve it. A different reviewer must sign off.';
    case 'signature_name_mismatch':
      return 'The typed signature must match your full name on file exactly.';
    case 'sop_not_found':
      return 'That SOP no longer exists — refresh the page.';
    case 'version_not_found':
      return 'That revision no longer exists — refresh the page.';
    case 'validation_failed':
      return 'Some fields look invalid — double-check and retry.';
    case 'reason_for_change_required':
      return 'A reason for change is required.';
    case 'invalid_target':
      return 'One of the applicability targets points at a role, seat, or department that no longer exists.';
    case 'file_not_found':
      return 'The uploaded file could not be found — try uploading it again.';
    case 'unsupported_file':
      return 'Only Word (.docx) files can be imported as SOPs.';
    case 'file_too_large':
      return 'That file is too large to import — SOP documents must be under 10 MB.';
    case 'conversion_failed':
      return 'That file could not be converted — it may be corrupt or password-protected.';
    case 'storage_disabled':
      return 'File storage isn’t configured, so files can’t be uploaded or imported.';
    case 'ai_disabled':
      return 'AI assistance isn’t configured for this organization.';
    case 'ai_error':
      return 'The assistant is temporarily unavailable — try again in a moment.';
    case 'ai_could_not_suggest':
      return 'The assistant couldn’t suggest applicability for this SOP — add it manually.';
    case 'too_many_files':
      return 'Too many files at once — upload them in smaller batches.';
    default:
      return eosErrorMessage(err) ?? (err instanceof Error ? err.message : fallback);
  }
}

// ── Re-acknowledgment status (ADR-038) ─────────────────────────────────────
//
// When a SOP revision is approved (published or a superseding update), the
// people responsible for it must (re-)acknowledge the new revision. This read
// model summarizes progress for the SOP's *current approved* revision: how many
// of the tracked audience have acknowledged and who is still outstanding (with
// an overdue flag once past `dueAt`). `total` is 0 for SOPs that predate
// propagation tracking. Kept in lockstep with the server contract in
// `server/src/modules/sops/propagation.ts` (`reackStatusForSop`).

export interface ReackStatusMember {
  membershipId: string;
  name: string;
  /** ISO instant the re-acknowledgment is due by. */
  dueAt: string;
  overdue: boolean;
}

export interface ReackStatus {
  /** The current approved version this status is for, or `null` when none. */
  sopVersionId: string | null;
  revision: number | null;
  total: number;
  acknowledged: number;
  outstanding: ReackStatusMember[];
}

/**
 * Re-acknowledgment progress for a SOP's current approved revision. Author-only
 * (server-gated `requireEditor`); the UI only queries it for editors viewing an
 * approved SOP.
 */
export async function getSopReackStatus(slug: string): Promise<ReackStatus> {
  const res = await apiClient.get<{ data: ReackStatus }>(
    `/api/compliance/sops/${encodeURIComponent(slug)}/reack-status`,
  );
  return res.data.data;
}

// ── Comprehension test (ADR-040) ─────────────────────────────────────────
//
// A per-SOP multiple-choice test. When it is ACTIVE, passing it is what
// satisfies the re-acknowledgment obligation — the plain acknowledge endpoint
// refuses with `comprehension_required` while a test gates the SOP, and a pass
// writes the Part-11 acknowledgment itself.
//
// Two shapes come back from GET depending on the caller: compliance editors
// (visionary/integrator/quality_lead) get `correctOptionId` + `explanation`;
// everyone else gets the taker view with both stripped, because qa_personnel
// and auditors may have to sit the test.

export interface ComprehensionOption {
  id: string;
  text: string;
}

export interface ComprehensionQuestion {
  id: string;
  order: number;
  prompt: string;
  optionsJson: unknown;
  aiDrafted: boolean;
  /** Editor view only. */
  correctOptionId?: string;
  /** Editor view only; also returned to a taker AFTER grading. */
  explanation?: string | null;
}

export interface ComprehensionTest {
  id: string;
  sopId: string;
  passThresholdPercent: number;
  maxAttempts: number | null;
  active: boolean;
  authorNameSnapshot: string;
  questions: ComprehensionQuestion[];
}

/** Narrow `optionsJson` (typed `unknown` to match the server) into options. */
export function readOptions(optionsJson: unknown): ComprehensionOption[] {
  if (!Array.isArray(optionsJson)) return [];
  const out: ComprehensionOption[] = [];
  for (const raw of optionsJson) {
    if (typeof raw !== 'object' || raw === null) continue;
    const opt = raw as Record<string, unknown>;
    if (typeof opt.id === 'string' && typeof opt.text === 'string') {
      out.push({ id: opt.id, text: opt.text });
    }
  }
  return out;
}

/** `null` when the SOP has no test at all. */
export async function getComprehensionTest(slug: string): Promise<ComprehensionTest | null> {
  const res = await apiClient.get<{ data: ComprehensionTest | null }>(
    `/api/compliance/sops/${encodeURIComponent(slug)}/comprehension`,
  );
  return res.data.data;
}

export interface DraftedQuestion {
  prompt: string;
  options: ComprehensionOption[];
  correctOptionId: string;
  explanation: string | null;
}

export interface SuggestQuestionsResponse {
  questions: DraftedQuestion[];
  /** Malformed drafts the server discarded — surfaced so it isn't silent. */
  droppedCount: number;
}

/** AI-draft questions from the SOP text. Persists NOTHING; the editor saves. */
export async function suggestComprehensionQuestions(
  slug: string,
  count?: number,
): Promise<SuggestQuestionsResponse> {
  const res = await apiClient.post<{ data: SuggestQuestionsResponse }>(
    `/api/compliance/sops/${encodeURIComponent(slug)}/comprehension/suggest`,
    count === undefined ? {} : { count },
  );
  return res.data.data;
}

export interface SaveComprehensionTestRequest {
  passThresholdPercent?: number;
  maxAttempts?: number | null;
  active?: boolean;
  questions: {
    prompt: string;
    options: ComprehensionOption[];
    correctOptionId: string;
    explanation?: string | null;
    aiDrafted?: boolean;
  }[];
}

/** Create or replace the test. Questions are replaced wholesale. */
export async function saveComprehensionTest(
  slug: string,
  body: SaveComprehensionTestRequest,
): Promise<ComprehensionTest> {
  const res = await apiClient.put<{ data: ComprehensionTest }>(
    `/api/compliance/sops/${encodeURIComponent(slug)}/comprehension`,
    body,
  );
  return res.data.data;
}

export interface ComprehensionAttemptResult {
  attemptId: string;
  scorePercent: number;
  passed: boolean;
  attemptNumber: number;
  /** True when passing also wrote (or found) the acknowledgment. */
  acknowledged: boolean;
  explanations: { questionId: string; explanation: string | null }[];
}

export async function submitComprehensionAttempt(
  slug: string,
  answers: { questionId: string; optionId: string }[],
  startedAt?: string,
): Promise<ComprehensionAttemptResult> {
  const res = await apiClient.post<{ data: ComprehensionAttemptResult }>(
    `/api/compliance/sops/${encodeURIComponent(slug)}/comprehension/attempts`,
    startedAt === undefined ? { answers } : { answers, startedAt },
  );
  return res.data.data;
}

// ── Revision requests (ADR-041) ──────────────────────────────────────────
//
// Any employee can propose a change to an SOP. The request walks the org's
// configurable approval chain (default: the requester's lead, then the
// integrator). Final approval optionally opens a draft revision, which then
// flows through the normal ADR-036 authoring workflow.

export type ChainStep =
  | { kind: 'lead' }
  | { kind: 'integrator' }
  | { kind: 'role'; role: string }
  | { kind: 'membership'; membershipId: string };

export type RevisionRequestStatus =
  | 'open'
  | 'in_review'
  | 'approved'
  | 'rejected'
  | 'withdrawn'
  | 'fulfilled';

export interface RevisionRequestDecision {
  stepIndex: number;
  approverNameSnapshot: string;
  decision: 'approved' | 'rejected';
  comment: string | null;
  decidedAt: string;
}

export interface RevisionRequest {
  id: string;
  sopId: string;
  summary: string;
  body: string;
  status: RevisionRequestStatus;
  currentStepIndex: number;
  /** The chain AS SNAPSHOTTED at submission — not the org's current config. */
  steps: ChainStep[];
  requesterNameSnapshot: string;
  requestedByMembershipId: string | null;
  openedVersionId: string | null;
  createdAt: string;
  closedAt: string | null;
  decisions: RevisionRequestDecision[];
}

export interface RevisionWorkflowConfig {
  steps: ChainStep[];
  autoOpenDraftOnApproval: boolean;
}

/** Human label for a chain step — mirrors the server's `describeStep`. */
export function describeChainStep(step: ChainStep): string {
  switch (step.kind) {
    case 'lead':
      return "The requester's lead";
    case 'integrator':
      return 'The integrator';
    case 'role':
      return `Anyone with the ${step.role.replace(/_/g, ' ')} role`;
    case 'membership':
      return 'A named approver';
  }
}

const BASE = '/api/compliance/sop-revision-requests';

export async function getRevisionWorkflowConfig(): Promise<RevisionWorkflowConfig> {
  const res = await apiClient.get<{ data: RevisionWorkflowConfig }>(`${BASE}/config`);
  return res.data.data;
}

/** Needs the `manage_sop_revisions` capability. */
export async function setRevisionWorkflowConfig(
  body: RevisionWorkflowConfig,
): Promise<RevisionWorkflowConfig> {
  const res = await apiClient.put<{ data: RevisionWorkflowConfig }>(`${BASE}/config`, body);
  return res.data.data;
}

/** Requests waiting on the caller, whichever step kind names them. */
export async function listRequestsAwaitingMe(): Promise<RevisionRequest[]> {
  const res = await apiClient.get<{ data: RevisionRequest[] }>(`${BASE}/awaiting-me`);
  return res.data.data;
}

export async function listRevisionRequests(slug: string): Promise<RevisionRequest[]> {
  const res = await apiClient.get<{ data: RevisionRequest[] }>(`${BASE}/sop/${encodeURIComponent(slug)}`);
  return res.data.data;
}

export async function submitRevisionRequest(
  slug: string,
  body: { summary: string; body: string },
): Promise<RevisionRequest> {
  const res = await apiClient.post<{ data: RevisionRequest }>(
    `${BASE}/sop/${encodeURIComponent(slug)}`,
    body,
  );
  return res.data.data;
}

export async function decideRevisionRequest(
  id: string,
  body: { decision: 'approved' | 'rejected'; comment?: string | null; reasonForChange?: string },
): Promise<RevisionRequest> {
  const res = await apiClient.post<{ data: RevisionRequest }>(
    `${BASE}/${encodeURIComponent(id)}/decide`,
    body,
  );
  return res.data.data;
}

export async function withdrawRevisionRequest(id: string): Promise<RevisionRequest> {
  const res = await apiClient.post<{ data: RevisionRequest }>(
    `${BASE}/${encodeURIComponent(id)}/withdraw`,
    {},
  );
  return res.data.data;
}

// ── Audit binder export ──────────────────────────────────────────────────

export type AuditBinderScope = 'kwf' | 'kwpl' | 'all';

export interface DownloadAuditBinderOptions {
  scope?: AuditBinderScope;
  /** YYYY-MM-DD; defaults server-side to one year before `until`. */
  since?: string;
  /** YYYY-MM-DD; defaults server-side to today. */
  until?: string;
}

/**
 * Download the audit binder ZIP. Pulls the binary as a Blob (so we can
 * surface server-side errors as toasts instead of navigating away from
 * the page mid-download), then triggers a save via a hidden anchor.
 *
 * The server-side endpoint already writes the export to the audit log
 * before streaming the response — no extra logging is needed here.
 */
export async function downloadAuditBinder(opts: DownloadAuditBinderOptions = {}): Promise<void> {
  const params = new URLSearchParams();
  if (opts.scope) params.set('serviceLine', opts.scope);
  if (opts.since) params.set('since', opts.since);
  if (opts.until) params.set('until', opts.until);
  const query = params.toString() ? `?${params.toString()}` : '';

  const res = await apiClient.get<Blob>(`/api/compliance/audit-binder/export${query}`, {
    responseType: 'blob',
  });

  // Pull a filename out of Content-Disposition. Servers quote the
  // filename, so accept both `filename="x"` and `filename=x`.
  const cd = typeof res.headers['content-disposition'] === 'string' ? res.headers['content-disposition'] : '';
  const match = /filename="?([^"]+)"?/.exec(cd);
  const filename =
    match?.[1] ?? `audit-binder_${opts.scope ?? 'kwf'}_${new Date().toISOString().slice(0, 10)}.zip`;

  // Trigger the save. createObjectURL → click → revoke is the canonical
  // pattern; works on Safari + Firefox + Chrome without library help.
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
